import React, { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { Box, IconButton, Stack, TextField, Tooltip } from '@mui/material';
import { Delete, Save } from '@mui/icons-material';
import { Button, EntityAutocomplete } from 'c-components';
import type { CampaignReportingConfigType } from 'c-sdk';
import { useCampaignReportingConfigs } from './useCampaignReportingConfigs';

type Props = {
    type: CampaignReportingConfigType;
    campaignType?: string;
    getConfig: () => string;
    // The saved configuration's name comes along for callers that label what's loaded.
    onLoad: (config: string, name: string) => void;
    onSaved?: (name: string) => void;
    // Controls the "Save as…" name, for a caller that saves from a button of its own.
    name?: string;
    onNameChange?: (name: string) => void;
};

export type ReportingConfigBarHandle = {
    // Saves under the "Save as…" name, as the bar's own Save does; false when it didn't.
    save: () => Promise<boolean>;
};

const ReportingConfigBar = forwardRef<ReportingConfigBarHandle, Props>(function ReportingConfigBar(
    { type, campaignType, getConfig, onLoad, onSaved, name: nameProp, onNameChange },
    ref,
) {
    const { configs, save, remove, isSaving } = useCampaignReportingConfigs(type, campaignType);
    const [ownName, setOwnName] = useState('');
    const name = nameProp ?? ownName;
    const setName = onNameChange ?? setOwnName;
    const [selectedId, setSelectedId] = useState<number | null>(null);

    const defaultFilters = useMemo(
        () => ({
            'filter.type': [type],
            ...(campaignType ? { 'filter.campaign_type': [campaignType] } : {}),
        }),
        [type, campaignType],
    );

    const selected = useMemo(
        () => configs.find(c => c.id === selectedId) ?? null,
        [configs, selectedId],
    );

    const handleSave = async (): Promise<boolean> => {
        const trimmed = name.trim();
        if (!trimmed) return false;
        const existing = configs.find(c => c.name.trim().toLowerCase() === trimmed.toLowerCase());
        const ok = await save(trimmed, getConfig(), existing?.id);
        if (ok) onSaved?.(trimmed);
        return ok;
    };
    useImperativeHandle(ref, () => ({ save: handleSave }));

    return (
        <Stack
            direction={{ xs: 'column', md: 'row' }}
            gap={1}
            alignItems={{ md: 'center' }}
            flexWrap="wrap"
        >
            <Box sx={{ minWidth: 260 }}>
                <EntityAutocomplete
                    entityName="CampaignReporting"
                    searchColumns="name"
                    labelColumn="name"
                    valueColumn="id"
                    defaultFilters={defaultFilters}
                    autoLoadSearchTerm=""
                    value={selectedId ?? ''}
                    onChange={(val: any) => {
                        const id = val ? Number(val) : null;
                        setSelectedId(id);
                        const cfg = configs.find(c => c.id === id);
                        if (cfg) setName(cfg.name);
                    }}
                    textFieldProps={{ label: 'Search saved configurations' }}
                />
            </Box>
            <Button
                variant="outlined"
                size="small"
                disabled={!selected}
                onClick={() => selected && onLoad(selected.config, selected.name)}
            >
                Load
            </Button>
            <Tooltip title="Delete saved configuration">
                <span>
                    <IconButton
                        size="small"
                        disabled={!selected}
                        onClick={async () => {
                            if (!selected) return;
                            await remove(selected.id);
                            setSelectedId(null);
                            setName('');
                        }}
                    >
                        <Delete fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
            <Box sx={{ flex: 1 }} />
            <TextField
                size="small"
                label="Save as…"
                value={name}
                onChange={e => setName(e.target.value)}
                sx={{ minWidth: 180 }}
            />
            <Button
                variant="contained"
                size="small"
                startIcon={<Save fontSize="small" />}
                disabled={!name.trim() || isSaving}
                onClick={handleSave}
            >
                Save
            </Button>
        </Stack>
    );
});

export default ReportingConfigBar;
